/**
 * Urgent session mock exam of a month (all centres, or one): the AI searches the web for the
 * subjects of past TCF Canada sessions (every week at night, or on demand here), then designs a
 * complete ORIGINAL mock exam with the official structure (39 CO, 39 CE from the bank, 3 EE and
 * 3 EO topics written by the AI, as drafts). The subjects found online are never reproduced.
 * The admin reads it and sends it by hand; nothing can be edited here and nothing leaves without
 * that click. The AI topics are then published (anti-plagiarism filter) and the learners of the
 * centre are told on WhatsApp.
 */

import {
  type MonthlyPack,
  useAcademyMockExamsRetrieve,
  useAcademyMonthlyPacksBuildCreate,
  useAcademyMonthlyPacksList,
  useAcademyMonthlyPacksSendCreate,
  useAcademyTasksRetrieve,
} from "@agencyos/api-client";
import { Badge, Button, Skeleton } from "@agencyos/ui-web";
import { CheckCircle2, ClipboardList, ExternalLink, Globe, Send, Sparkles, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { dateTimeText } from "../billing/shared";
import { type ExamSkill, type PackAnalysis, groupFound, monthLabel, parsePack, stringList, structureCheck } from "./academyData";
import { useAdminExam } from "./examContext";
import { useExpectedStructure } from "./MockExamsTab";
import { ErrorList, isObject, Pager, StatusBadge, str, useAcademyRoles, useRefreshAcademy } from "./shared";

const WRITTEN: ExamSkill[] = ["ee", "eo"];

function SourceBadge({ source }: { source: string }) {
  const { t } = useTranslation();
  return source === "ai" ? (
    <Badge tone="offer">
      <Sparkles size={14} aria-hidden="true" /> {t("academyAdmin.urgent.sourceAi")}
    </Badge>
  ) : (
    <Badge>{t("academyAdmin.urgent.sourceRules")}</Badge>
  );
}

/** One EE/EO topic written by the AI (French, as the exam), read from academy/tasks/{id}. */
function TopicPreview({ id }: { id: string }) {
  const { t } = useTranslation();
  const query = useAcademyTasksRetrieve(id);
  if (query.data === undefined) return query.isError ? <p className="ko">{t("common.error")}</p> : <Skeleton style={{ height: 72 }} />;
  if (query.data.status !== 200) return <p className="ko">{t("common.error")}</p>;
  const task = query.data.data;
  const documents = stringList(task.documents);
  const script = isObject(task.examiner_script) ? task.examiner_script : {};
  const followUps = stringList(script.follow_ups);
  const role = str(script.role);
  return (
    <li className="installment-row stack">
      <div className="row between">
        <strong>
          {task.skill.toUpperCase()} · {t("academyAdmin.topics.task", { n: task.number })}
          {task.theme ? (
            <span className="muted" lang="fr">
              {" "}
              · {task.theme}
            </span>
          ) : null}
        </strong>
        <StatusBadge status={task.status} />
      </div>
      <p className="academy-pre" lang="fr">
        {task.prompt}
      </p>
      {documents.map((doc, i) => (
        <blockquote key={i} className="academy-pre muted" lang="fr">
          {doc}
        </blockquote>
      ))}
      {role ? (
        <p className="muted">
          {t("academyAdmin.urgent.role")} <span lang="fr">{role}</span>
        </p>
      ) : null}
      {followUps.length ? (
        <div className="stack">
          <span className="muted">{t("academyAdmin.urgent.followUps")}</span>
          <ul lang="fr">
            {followUps.map((q) => (
              <li key={q}>{q}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </li>
  );
}

/** The session mock exam: structure against the official format, then the 6 EE/EO topics. */
function ExamPreview({ id, onOpenMocks }: { id: string; onOpenMocks?: () => void }) {
  const { t } = useTranslation();
  const expected = useExpectedStructure();
  const query = useAcademyMockExamsRetrieve(id);
  if (query.data === undefined) return query.isError ? <p className="ko">{t("common.error")}</p> : <Skeleton style={{ height: 160 }} />;
  if (query.data.status !== 200) return <p className="ko">{t("common.error")}</p>;
  const exam = query.data.data;
  const check = expected ? structureCheck(exam.counts, expected) : null;
  const topics = isObject(exam.tasks) ? exam.tasks : {};
  return (
    <section className="stack" aria-labelledby={`session-exam-${exam.id}`}>
      <div className="section-title academy-wrap">
        <div>
          <h4 id={`session-exam-${exam.id}`}>{t("academyAdmin.urgent.examTitle")}</h4>
          <p className="muted" lang="fr">
            {exam.title}
          </p>
        </div>
        {onOpenMocks ? (
          <Button variant="ghost" icon={<ClipboardList size={16} aria-hidden="true" />} onClick={onOpenMocks}>
            {t("academyAdmin.urgent.openMocks")}
          </Button>
        ) : null}
      </div>
      <div className="row">
        {(["co", "ce", "ee", "eo"] as const).map((s) => {
          const row = check?.rows.find((r) => r.skill === s);
          return (
            <span key={s} className="code-chip">
              {s.toUpperCase()} {exam.counts?.[s] ?? 0}
              {row ? ` / ${row.expected}` : ""}
            </span>
          );
        })}
        {check ? (
          check.ok ? (
            <Badge tone="success">
              <CheckCircle2 size={14} aria-hidden="true" /> {t("academyAdmin.mocks.compliant")}
            </Badge>
          ) : (
            <Badge tone="danger">
              <TriangleAlert size={14} aria-hidden="true" /> {t("academyAdmin.mocks.nonCompliant")}
            </Badge>
          )
        ) : null}
      </div>
      <p className="muted">{t("academyAdmin.urgent.bankHint")}</p>
      {WRITTEN.map((skill) => {
        const ids = stringList(topics[skill]);
        return (
          <section key={skill} className="stack" aria-labelledby={`session-topics-${skill}`}>
            <h5 id={`session-topics-${skill}`}>{t("academyAdmin.urgent.topicsOf", { skill: t(`academyAdmin.skill.${skill}`) })}</h5>
            {ids.length ? (
              <ol className="plain-list stack">
                {ids.map((taskId) => (
                  <TopicPreview key={taskId} id={taskId} />
                ))}
              </ol>
            ) : (
              <p className="muted">{t("academyAdmin.urgent.noTopics")}</p>
            )}
          </section>
        );
      })}
    </section>
  );
}

function SendBlock({ pack, onSent }: { pack: MonthlyPack; onSent: (next: MonthlyPack) => void }) {
  const { t } = useTranslation();
  const refresh = useRefreshAcademy();
  const send = useAcademyMonthlyPacksSendCreate({ mutation: { onSuccess: refresh } });
  const [confirming, setConfirming] = useState(false);
  const center = pack.center || t("academyAdmin.urgent.everyCenter");

  const onConfirm = () =>
    send.mutate(
      { id: pack.id },
      {
        onSuccess: (response) => {
          if (response.status === 200) onSent(response.data);
        },
        onSettled: () => setConfirming(false),
      },
    );

  return (
    <div className="stack">
      {confirming ? (
        <div className="notice-warning installment-row stack" role="group" aria-labelledby={`confirm-send-${pack.id}`}>
          <strong id={`confirm-send-${pack.id}`}>{t("academyAdmin.urgent.confirmTitle", { center })}</strong>
          <p>{t("academyAdmin.urgent.confirmText", { center })}</p>
          <div className="row">
            <Button icon={<Send size={16} aria-hidden="true" />} disabled={send.isPending} onClick={onConfirm}>
              {send.isPending ? t("common.loading") : t("academyAdmin.urgent.confirm")}
            </Button>
            <Button variant="ghost" disabled={send.isPending} onClick={() => setConfirming(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="row">
          <Button
            icon={<Send size={16} aria-hidden="true" />}
            onClick={() => {
              send.reset();
              setConfirming(true);
            }}
          >
            {t("academyAdmin.urgent.send", { center })}
          </Button>
        </div>
      )}
      {send.isError ? (
        <div className="stack">
          <strong className="ko">{t("academyAdmin.urgent.sendRefused")}</strong>
          <ErrorList error={send.error} />
        </div>
      ) : null}
    </div>
  );
}

/** What the AI found online: subjects by skill and task, its notes, the pages it read. */
function Research({ analysis, id }: { analysis: PackAnalysis; id: string }) {
  const { t } = useTranslation();
  const groups = groupFound(analysis.foundOnline);
  return (
    <section className="stack" aria-labelledby={`found-${id}`}>
      <h4 id={`found-${id}`}>
        <Globe size={16} aria-hidden="true" /> {t("academyAdmin.urgent.found")}
      </h4>
      <p role="note" className="notice-warning installment-row">
        {t("academyAdmin.urgent.neverCopied")}
      </p>
      {groups.length ? (
        <ul className="plain-list stack">
          {groups.map((group) => (
            <li key={group.key} className="installment-row stack">
              <strong>{t("academyAdmin.urgent.foundGroup", { skill: group.skill.toUpperCase(), n: group.task })}</strong>
              <ul className="plain-list stack">
                {group.items.map((item, i) => (
                  <li key={i} className="stack">
                    <span lang="fr">
                      {item.theme ? <strong>{item.theme} · </strong> : null}
                      {item.summary}
                    </span>
                    {item.session || item.where ? (
                      <span className="muted">{[item.session, item.where].filter(Boolean).join(" · ")}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">{t("academyAdmin.urgent.foundNone")}</p>
      )}
      <div className="stack">
        <h5>{t("academyAdmin.urgent.notes")}</h5>
        {analysis.researchNotes ? (
          <p className="academy-pre" lang="fr">
            {analysis.researchNotes}
          </p>
        ) : (
          <p className="muted">{t("academyAdmin.urgent.noNotes")}</p>
        )}
      </div>
      {analysis.sources.length ? (
        <div className="stack">
          <h5>{t("academyAdmin.urgent.sources")}</h5>
          <ul className="plain-list stack">
            {analysis.sources.map((src) => (
              <li key={src.url} className="academy-source">
                <a className="link" href={src.url} target="_blank" rel="noopener noreferrer">
                  {src.title} <ExternalLink size={14} aria-hidden="true" />
                  <span className="visually-hidden"> {t("academyAdmin.urgent.newTab")}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

/** Analysis and exam of one pack (month × centre). */
function PackPanel({ pack, onOpenMocks }: { pack: MonthlyPack; onOpenMocks?: () => void }) {
  const { t, i18n } = useTranslation();
  const { canEdit } = useAcademyRoles();
  const [current, setCurrent] = useState<MonthlyPack>(pack);
  const [justSent, setJustSent] = useState(false);
  const analysis = parsePack(current.analysis);
  const sent = current.status === "published";
  return (
    <div className="stack installment-row" aria-live="polite">
      <div className="row">
        {sent ? <Badge tone="success">{t("academyAdmin.urgent.sent")}</Badge> : <Badge>{t("academyAdmin.urgent.draft")}</Badge>}
        <SourceBadge source={analysis.source} />
        <Badge>{current.center || t("academyAdmin.sessions.allCenters")}</Badge>
        <span className="muted">
          {monthLabel(current.month)}
          {current.feedback_count > 0 ? ` · ${t("academyAdmin.urgent.basedOn", { count: current.feedback_count })}` : ""}
        </span>
      </div>
      <Research analysis={analysis} id={current.id} />
      <section className="stack" aria-labelledby={`ai-summary-${current.id}`}>
        <h4 id={`ai-summary-${current.id}`}>{t("academyAdmin.urgent.summary")}</h4>
        {analysis.summary ? (
          <p className="academy-pre" lang="fr">
            {analysis.summary}
          </p>
        ) : (
          <p className="muted">{t("academyAdmin.urgent.noSummary")}</p>
        )}
      </section>
      {analysis.grouped.length ? (
        <section className="stack" aria-labelledby={`ai-themes-${current.id}`}>
          <h4 id={`ai-themes-${current.id}`}>{t("academyAdmin.urgent.grouped")}</h4>
          <ul className="plain-list academy-chip-list">
            {analysis.grouped.map((g) => (
              <li key={`${g.skill}-${g.theme}`} className="code-chip">
                {g.skill.toUpperCase()} · <span lang="fr">{g.theme}</span> · {t("academyAdmin.trends.reports", { count: g.count })}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {analysis.structure ? (
        <p role="alert" className="notice-warning installment-row">
          {t("academyAdmin.urgent.structureKo", { detail: analysis.structure })}
        </p>
      ) : null}
      {current.mock_exam ? <ExamPreview id={current.mock_exam} onOpenMocks={onOpenMocks} /> : null}
      {sent ? (
        <p role="status" className={justSent ? "ok" : "muted"}>
          {justSent
            ? t("academyAdmin.urgent.sentNow", { count: current.notified })
            : t("academyAdmin.urgent.sentOn", { date: current.published_at ? dateTimeText(current.published_at, i18n.language) : "—", count: current.notified })}
        </p>
      ) : canEdit ? (
        <SendBlock
          pack={current}
          onSent={(next) => {
            setCurrent(next);
            setJustSent(true);
          }}
        />
      ) : null}
    </div>
  );
}

export function SessionExam({
  month,
  center,
  onSelect,
  onOpenMocks,
}: {
  month: string;
  center: string;
  onSelect: (month: string, center: string) => void;
  onOpenMocks?: () => void;
}) {
  const { t } = useTranslation();
  const { canEdit } = useAcademyRoles();
  const refresh = useRefreshAcademy();
  const [page, setPage] = useState(1);
  const [built, setBuilt] = useState<MonthlyPack | null>(null);
  const [builds, setBuilds] = useState(0);
  const { params, contentParams } = useAdminExam();
  const list = useAcademyMonthlyPacksList({ page, ...contentParams });
  const build = useAcademyMonthlyPacksBuildCreate({ mutation: { onSuccess: refresh } });
  const data = list.data?.status === 200 ? list.data.data : undefined;
  const validMonth = /^\d{4}-\d{2}$/.test(month);
  const matches = (p: MonthlyPack) => p.month === month && p.center === center;
  const pack = built && matches(built) ? built : data?.results.find(matches);
  const centerName = center || t("academyAdmin.sessions.allCenters");

  const onBuild = () =>
    build.mutate(
      { data: { month, ...(center ? { center } : {}), ...params } },
      {
        onSuccess: (response) => {
          if (response.status !== 201) return;
          setBuilt(response.data);
          setBuilds((n) => n + 1);
        },
      },
    );

  return (
    <section className="stack academy-session" aria-labelledby="session-exam-title">
      <div className="section-title academy-wrap">
        <div>
          <h3 id="session-exam-title">{t("academyAdmin.urgent.title", { center: centerName })}</h3>
          <p className="muted">{t("academyAdmin.urgent.intro")}</p>
        </div>
        {canEdit && pack?.status !== "published" ? (
          <Button icon={<Sparkles size={16} aria-hidden="true" />} disabled={build.isPending || !validMonth} onClick={onBuild}>
            {build.isPending ? t("academyAdmin.urgent.building") : pack ? t("academyAdmin.urgent.rebuild") : t("academyAdmin.urgent.build")}
          </Button>
        ) : null}
      </div>
      <p className="muted">{t("academyAdmin.urgent.nightly")}</p>
      <ErrorList error={build.error} />
      {data === undefined && !built ? (
        <Skeleton style={{ height: 160 }} />
      ) : pack ? (
        <PackPanel key={`${pack.id}-${builds}`} pack={pack} onOpenMocks={onOpenMocks} />
      ) : (
        <p className="muted">{t("academyAdmin.urgent.none", { month: monthLabel(month), center: centerName })}</p>
      )}
      {data?.results.length ? (
        <section className="stack" aria-labelledby="session-exams-history">
          <h4 id="session-exams-history">{t("academyAdmin.urgent.history")}</h4>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("academyAdmin.urgent.month")}</th>
                  <th>{t("academyAdmin.sessions.center")}</th>
                  <th>{t("academyAdmin.urgent.reports")}</th>
                  <th>{t("academyAdmin.urgent.source")}</th>
                  <th>{t("academyAdmin.statusLabel")}</th>
                  <th>{t("academyAdmin.urgent.notified")}</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((row) => (
                  <tr key={row.id}>
                    <td className="nowrap">
                      <button type="button" className="link" onClick={() => onSelect(row.month, row.center)}>
                        {monthLabel(row.month)}
                      </button>
                    </td>
                    <td>{row.center || t("academyAdmin.sessions.allCenters")}</td>
                    <td>{row.feedback_count}</td>
                    <td>
                      <SourceBadge source={parsePack(row.analysis).source} />
                    </td>
                    <td>{row.status === "published" ? <Badge tone="success">{t("academyAdmin.urgent.sent")}</Badge> : <Badge>{t("academyAdmin.urgent.draft")}</Badge>}</td>
                    <td>{row.status === "published" ? row.notified : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} />
        </section>
      ) : null}
    </section>
  );
}
